import { useState, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  Copy, Mail, Inbox, FileText, Download, ExternalLink, Code2} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";

type InboundStatus =
  | "received" | "routed" | "unrouted"
  | "signature_failed" | "processing_error"
  | "extracting" | "extracted" | "failed";

interface InboundMessage {
  id: string;
  recipient: string;
  sender: string;
  fromHeader: string | null;
  subject: string | null;
  status: InboundStatus;
  error: string | null;
  flowType: string | null;
  attachmentCount: number;
  receivedAt: string;
  processedAt: string | null;
  bodyPlain: string | null;
  rawPayload: Record<string, unknown> | null;
}

interface InboundAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: "queued" | "extracted" | "processing_error" | "skipped";
  flowType: string | null;
  createdEntityType: string | null;
  createdEntityId: string | null;
  error: string | null;
}

const STATUS_VARIANT: Record<InboundStatus, "default" | "secondary" | "outline" | "destructive"> = {
  received: "secondary", routed: "default", unrouted: "outline",
  signature_failed: "destructive", processing_error: "destructive",
  extracting: "secondary", extracted: "default", failed: "destructive",
};
const STATUS_LABEL: Record<InboundStatus, string> = {
  received: "Received", routed: "Routed", unrouted: "Unrouted",
  signature_failed: "Signature failed", processing_error: "Processing error",
  extracting: "Processing", extracted: "Extracted", failed: "Failed",
};

const ENTITY_DETAIL_PREFIX: Record<string, string> = {
  customer_po: "/customer-pos",
  vendor_quote: "/vendor-quotes",
  ap_invoice: "/ap-invoices",
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function InboundEmailPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [senderFilter, setSenderFilter] = useState("");
  const [afterFilter, setAfterFilter] = useState("");
  const [beforeFilter, setBeforeFilter] = useState("");

  const { data: address } = useQuery<{ address: string; slug: string }>({
    queryKey: ["/api/inbound-email/address"],
    queryFn: () => api.get<{ address: string; slug: string }>(`/inbound-email/address`, sessionId),
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (senderFilter.trim()) p.set("senderContains", senderFilter.trim());
    if (afterFilter) p.set("receivedAfter", new Date(afterFilter).toISOString());
    if (beforeFilter) p.set("receivedBefore", new Date(beforeFilter).toISOString());
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [senderFilter, afterFilter, beforeFilter]);

  const { data: messagesData } = useQuery<{ messages: InboundMessage[] }>({
    queryKey: ["/api/inbound-email/messages", queryString],
    queryFn: () => api.get<{ messages: InboundMessage[] }>(`/inbound-email/messages${queryString}`, sessionId),
  });

  const { data: detail } = useQuery<{ message: InboundMessage; attachments: InboundAttachment[] }>({
    queryKey: ["/api/inbound-email/messages", selectedId],
    queryFn: () => api.get<{ message: InboundMessage; attachments: InboundAttachment[] }>(`/inbound-email/messages/${selectedId}`, sessionId),
    enabled: !!selectedId,
  });

  const copyAddress = async () => {
    if (!address?.address) return;
    try {
      await navigator.clipboard.writeText(address.address);
      toast({ title: "Copied", description: "Inbox address copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inbound Email"
        description="Forward customer POs, vendor quotes, or AP invoices to your inbox to have them queued for AI extraction."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-medium">
            <Mail className="h-4 w-4" /> Your inbox address
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Anyone can email this address — every received message lands in the log below.
          </p>
        </CardHeader>
        <CardContent>
          {address ? (
            <div className="flex items-center gap-2">
              <code className="rounded-md bg-muted px-3 py-2 text-sm font-mono">{address.address}</code>
              <Button variant="outline" size="sm" onClick={copyAddress}>
                <Copy className="mr-1 h-3.5 w-3.5" /> Copy
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Loading...</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-medium">
            <Inbox className="h-4 w-4" /> Recent messages
          </CardTitle>
          <p className="text-sm text-muted-foreground">The most recent messages received at your inbox.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="text-xs">Sender contains</label>
              <Input value={senderFilter} onChange={(e) => setSenderFilter(e.target.value)} placeholder="e.g. acme.com" />
            </div>
            <div className="space-y-1">
              <label className="text-xs">Received after</label>
              <Input type="date" value={afterFilter} onChange={(e) => setAfterFilter(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs">Received before</label>
              <Input type="date" value={beforeFilter} onChange={(e) => setBeforeFilter(e.target.value)} />
            </div>
          </div>

          {messagesData?.messages?.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead className="text-right">Attachments</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messagesData.messages.map((m) => (
                  <TableRow
                    key={m.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => { setSelectedId(m.id); setShowRaw(false); }}
                  >
                    <TableCell className="text-sm">
                      {formatDistanceToNow(new Date(m.receivedAt), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-sm">{m.sender}</TableCell>
                    <TableCell className="text-sm max-w-[400px] truncate">
                      {m.subject || <span className="italic text-muted-foreground">(no subject)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-right">{m.attachmentCount ?? 0}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[m.status]}>{STATUS_LABEL[m.status]}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No messages yet. Try forwarding an email to your inbox.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedId} onOpenChange={(o) => { if (!o) { setSelectedId(null); setShowRaw(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Email details</DialogTitle>
          </DialogHeader>
          {detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
                <span className="text-muted-foreground">From</span>
                <span>{detail.message.fromHeader || detail.message.sender}</span>
                <span className="text-muted-foreground">To</span>
                <span>{detail.message.recipient}</span>
                <span className="text-muted-foreground">Subject</span>
                <span>{detail.message.subject || <em>(no subject)</em>}</span>
                <span className="text-muted-foreground">Status</span>
                <span><Badge variant={STATUS_VARIANT[detail.message.status]}>{STATUS_LABEL[detail.message.status]}</Badge></span>
                {detail.message.error && (
                  <>
                    <span className="text-muted-foreground">Error</span>
                    <span className="text-destructive text-xs">{detail.message.error}</span>
                  </>
                )}
              </div>

              {detail.attachments.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Attachments</h3>
                  <div className="space-y-1">
                    {detail.attachments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>{a.filename}</span>
                          <span className="text-xs text-muted-foreground">({formatSize(a.sizeBytes)})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {a.createdEntityType && a.createdEntityId && ENTITY_DETAIL_PREFIX[a.createdEntityType] && (
                            <a
                              href={`${ENTITY_DETAIL_PREFIX[a.createdEntityType]}/${a.createdEntityId}`}
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" /> Open draft
                            </a>
                          )}
                          <Badge variant={a.status === "extracted" ? "default" : a.status === "processing_error" ? "destructive" : "outline"}>
                            {a.status}
                          </Badge>
                          <Download className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.message.bodyPlain && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Message body</h3>
                  <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                    {detail.message.bodyPlain}
                  </pre>
                </div>
              )}

              <div className="space-y-2">
                <Button variant="outline" size="sm" onClick={() => setShowRaw((v) => !v)}>
                  <Code2 className="mr-1 h-3.5 w-3.5" />
                  {showRaw ? "Hide raw payload" : "View raw payload"}
                </Button>
                {showRaw && (
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                    {JSON.stringify(detail.message.rawPayload, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Loading...</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
